import React, { useEffect, useMemo, useState } from "react";
import { Container, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import SecondaryButton from "../../../components/Common/SecondaryButton";
import EmptyState from "../../../components/Common/EmptyState";
import { get } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import arrowRight from "../../../assets/images/authentication/arrow-right.svg";

/* ============ inline icons (self-contained, no new asset files needed) ============ */

const sp = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
const IconGift = () => (<svg {...sp}><rect x="4" y="9" width="16" height="11" rx="1" /><path d="M2 9h20v3H2z" /><path d="M12 9v11M12 9C10 5 5 6 6 9c1 1.5 6 0 6 0zM12 9c2-4 7-3 6 0-1 1.5-6 0-6 0z" /></svg>);
const IconPercent = () => (<svg {...sp}><circle cx="7" cy="7" r="2.3" /><circle cx="17" cy="17" r="2.3" /><path d="M18 6 6 18" /></svg>);
const IconDollarCircle = () => (<svg {...sp}><circle cx="12" cy="12" r="9" /><path d="M12 7v10M9.5 9.5c0-1.4 1.2-2.5 2.5-2.5s2.5 1 2.5 2c0 3-5 2-5 5 0 1 1.1 2 2.5 2s2.5-1.1 2.5-2.5" /></svg>);
const IconPeople = () => (<svg {...sp}><circle cx="9" cy="8" r="3" /><path d="M2 20c0-3.3 3.1-6 7-6s7 2.7 7 6" /><circle cx="17" cy="9" r="2.4" /><path d="M17.5 14.2c2.6.4 4.5 2.6 4.5 5.3" /></svg>);
const IconCalendar = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>);
const IconChevronDown = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>);

/* ============ helpers ============ */

const pick = (obj, ...paths) => {
  for (const path of paths) {
    const value = path
      .split(".")
      .reduce((acc, key) => (acc === null || acc === undefined ? acc : acc[key]), obj);
    if (value !== undefined && value !== null) return value;
  }
  return null;
};

/** react-query's `data` is the raw axios response, so unwrap {data:{data:...}} defensively. */
const extractPayload = (queryData) => {
  if (!queryData) return null;
  if (typeof queryData.data?.data !== "undefined") return queryData.data.data;
  return queryData.data || null;
};

const currency = (value, fallback = "—") => {
  if (value === null || value === undefined || value === "") return fallback;
  const num = Number(value);
  if (Number.isNaN(num)) return fallback;
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const number = (value, fallback = "—") => {
  if (value === null || value === undefined || value === "") return fallback;
  const num = Number(value);
  if (Number.isNaN(num)) return fallback;
  return num.toLocaleString();
};

const pct = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return num;
};

const normalizeAmount = (value) => {
  if (value && typeof value === "object") {
    if (value.amount !== undefined && value.amount !== null) return value.amount;
    if (value.value !== undefined && value.value !== null) return value.value;
    if (value.cents !== undefined && value.cents !== null) return value.cents / 100;
    return null;
  }
  return value;
};

const formatDateShort = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const PERIOD_LABELS = {
  weekly: "Weekly",
  monthly: "Monthly",
  yearly: "Yearly",
  all_time: "All Time",
  custom: "Custom Range",
};

const getPeriodLabel = (period) => {
  const labels = {
    weekly: "Weekly",
    monthly: "Monthly",
    yearly: "Yearly",
    all_time: "All Time",
    custom: "Custom Range",
  };
  return labels[period] || "Weekly";
};

/* ============ small shared bits ============ */

const StatusBadge = ({ status }) => {
  const map = {
    paid: "stl-badge--green",
    completed: "stl-badge--green",
    settlement_completed: "stl-badge--green",
    active: "stl-badge--green",
    pending: "stl-badge--orange",
    upcoming: "stl-badge--blue",
    failed: "stl-badge--red",
    no_platform_fee: "stl-badge--grey",
  };
  const key = (status || "").toLowerCase();
  return <span className={`stl-badge ${map[key] || "stl-badge--grey"}`}>{status || "Unknown"}</span>;
};

const SummaryStatCard = ({ title, titleColor, icon, iconBg, value, deltaPct, deltaLabel, secondaryLabel, secondaryValue, periodLabel }) => (
  <div className="rp-card stl-stat-card">
    <div className="stl-stat-card__top">
      <span className="stl-stat-card__title" style={{ color: titleColor }}>
        {title}
      </span>
      <span className="stl-stat-card__icon" style={{ background: iconBg, color: titleColor }}>{icon}</span>
    </div>
    <div className="stl-stat-card__value">{value}</div>
    <div className="stl-stat-card__delta-row">
      <span className="stl-stat-card__delta-label">{periodLabel}</span>
      {deltaPct !== null ? (
        <span className={`stl-stat-card__delta ${deltaPct >= 0 ? "stl-stat-card__delta--up" : "stl-stat-card__delta--down"}`}>
          {deltaPct >= 0 ? "↑" : "↓"} {Math.abs(deltaPct)}% vs last period
        </span>
      ) : (
        <span className="stl-stat-card__delta stl-stat-card__delta--muted">vs last period —</span>
      )}
    </div>
    <div className="stl-stat-card__secondary">
      <span>{secondaryLabel}</span>
      <strong>{secondaryValue}</strong>
    </div>
  </div>
);

const Donut = ({ segments, centerValue, centerLabel, size = 160, stroke = 22 }) => {
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  let cumulative = 0;
  return (
    <div className="stl-donut" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--bg-light-grey)" strokeWidth={stroke} />
        {segments.map((seg) => {
          const dash = (seg.pct / 100) * circumference;
          const offset = circumference - (cumulative / 100) * circumference;
          cumulative += seg.pct;
          return (
            <circle
              key={seg.label}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={seg.color}
              strokeWidth={stroke}
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={offset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
              strokeLinecap="butt"
            />
          );
        })}
      </svg>
      <div className="stl-donut__center">
        <span className="stl-donut__value">{centerValue}</span>
        <span className="stl-donut__label">{centerLabel}</span>
      </div>
    </div>
  );
};

const SettlementDashboardPage = () => {
  document.title = "Settlements Dashboard | Omaha Food Lovers";
  const navigate = useNavigate();

  const getCurrentUserId = () => {
    const authUser = localStorage.getItem("authUser");
    if (authUser) {
      try {
        const user = JSON.parse(authUser);
        return user.id || user.user_id || user.email;
      } catch (e) {
        return null;
      }
    }
    return null;
  };
  const userId = getCurrentUserId();

  const [period, setPeriod] = useState("weekly");
  const [periodMenuOpen, setPeriodMenuOpen] = useState(false);
  const [dateRange, setDateRange] = useState({ preset: "custom", start: "", end: "" });
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const { data: dashboardData } = useQuery({
    queryKey: ["restaurantSettlementsDashboard", userId, period, dateRange],
    queryFn: () => {
      const params = new URLSearchParams();
      params.append("period", period);
      if (period === "custom") {
        if (dateRange?.start) params.append("date_from", dateRange.start);
        if (dateRange?.end) params.append("date_to", dateRange.end);
      }
      return get(`${endpoints.restaurantDashboardSettlementsImpactReport}?${params.toString()}`);
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: invoicesData, isLoading: invoicesLoading } = useQuery({
    queryKey: ["restaurantSettlementInvoices", userId, page, period, dateRange],
    queryFn: () => {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("per_page", 20);
      params.append("sort_by", "period_start");
      params.append("sort_direction", "desc");
      if (period === "custom") {
        if (dateRange?.start) params.append("date_from", dateRange.start);
        if (dateRange?.end) params.append("date_to", dateRange.end);
      }
      return get(`${endpoints.restaurantSettlementInvoices}?${params.toString()}`);
    },
    staleTime: 5 * 60 * 1000,
  });

  // Update loading state
  useEffect(() => {
    if (page === 1) {
      setLoading(invoicesLoading);
    } else {
      setLoadingMore(invoicesLoading);
    }
  }, [invoicesLoading, page]);

  const dashboardPayload = useMemo(() => {
    const payload = extractPayload(dashboardData) || {};
    return payload;
  }, [dashboardData]);
  const invoicesPayload = useMemo(() => extractPayload(invoicesData) || {}, [invoicesData]);

  // Update hasMore based on pagination response
  useEffect(() => {
    if (invoicesPayload) {
      const currentPage = invoicesPayload.current_page || 1;
      const lastPage = invoicesPayload.last_page || 1;
      setHasMore(currentPage < lastPage);
    }
  }, [invoicesPayload]);

  /* ---- top summary stat cards ---- */

  const summaryCards = pick(dashboardPayload, "summary_cards") || {};
  const totalMemberSpend = pick(summaryCards, "total_member_spend.amount");
  const totalRedeemed = pick(summaryCards, "voucher_redemption_amount.amount");
  const platformFee = pick(dashboardPayload, "summary.platform_fee.amount") || 0;
  const netRestaurantRevenue = pick(summaryCards, "restaurant_net_revenue.amount");
  const feeRatePct = 3;

  const totalTransactions = pick(summaryCards, "total_transactions.count") || pick(summaryCards, "total_member_spend.transaction_count");
  const totalRedemptions = pick(summaryCards, "voucher_redemption_amount.redemption_count");
  const settlementAmountPayable = pick(summaryCards, "settlement_amount_payable.amount") ?? 0;

  const deltaMemberSpend = pct(pick(summaryCards, "total_member_spend.comparison_percentage"));
  const deltaRedeemed = pct(pick(summaryCards, "voucher_redemption_amount.comparison_percentage"));
  const deltaFee = 0; // Not provided in restaurant API
  const deltaNetRevenue = pct(pick(summaryCards, "restaurant_net_revenue.comparison_percentage"));

  /* ---- settlement status donut (from settlement data) ---- */

  const statusBreakdown = useMemo(() => {
    const widgets = pick(dashboardPayload, "widgets") || {};
    const settlement = pick(dashboardPayload, "settlement") || {};
    const breakdown = pick(widgets, "status_breakdown") || pick(settlement, "status_breakdown") || [];
    const getStatusCount = (status, fallback) => {
      const row = Array.isArray(breakdown)
        ? breakdown.find((item) => String(item.status || item.label || "").toLowerCase() === status)
        : null;
      return pick(row, "count", "total") ?? fallback ?? 0;
    };
    const pendingPlatformFee = getStatusCount("pending", pick(settlement, "pending_platform_fee.count"));
    const paidPlatformFee = getStatusCount("completed", getStatusCount("paid", pick(settlement, "paid_platform_fee.count")));
    const failedPlatformFee = getStatusCount("failed", pick(settlement, "failed_platform_fee.count"));
    const totalPlatformFee = pick(widgets, "total_settlement_count")
      ?? pick(settlement, "total_platform_fee.count")
      ?? (pendingPlatformFee + paidPlatformFee + failedPlatformFee);

    if (totalPlatformFee === 0) return null;

    const colorMap = { completed: "#1ba97f", pending: "#f59e0b", failed: "#e23d3d" };

    const completedAmount = pick(widgets, "completed_platform_fee") ?? pick(settlement, "paid_platform_fee.amount") ?? 0;
    const pendingAmount = pick(widgets, "pending_platform_fee") ?? pick(settlement, "pending_platform_fee.amount") ?? 0;
    const failedAmount = pick(widgets, "failed_platform_fee") ?? pick(settlement, "failed_platform_fee.amount") ?? 0;

    return [
      {
        label: "Completed",
        color: colorMap.completed,
        count: paidPlatformFee,
        amount: completedAmount,
        pct: totalPlatformFee > 0 ? Math.round((paidPlatformFee / totalPlatformFee) * 1000) / 10 : 0,
      },
      {
        label: "Pending",
        color: colorMap.pending,
        count: pendingPlatformFee,
        amount: pendingAmount,
        pct: totalPlatformFee > 0 ? Math.round((pendingPlatformFee / totalPlatformFee) * 1000) / 10 : 0,
      },
      {
        label: "Failed",
        color: colorMap.failed,
        count: failedPlatformFee,
        amount: failedAmount,
        pct: totalPlatformFee > 0 ? Math.round((failedPlatformFee / totalPlatformFee) * 1000) / 10 : 0,
      },
    ];
  }, [dashboardPayload]);

  const totalRestaurantsSettled =
    pick(dashboardPayload, "widgets.total_settlement_count")
    ?? pick(dashboardPayload, "settlement.total_platform_fee.count")
    ?? 0;

  /* ---- restaurant settlements table (real rows, client-side search/sort/paginate) ---- */

  const [allBalanceRows, setAllBalanceRows] = useState([]);

  const balanceRows = useMemo(() => {
    const raw = pick(invoicesPayload, "data") || [];
    if (!Array.isArray(raw)) return [];
    return raw.map((item) => ({
      id: item.id,
      restaurantName: pick(item, "restaurant.title", "restaurant.legal_business_name", "restaurant.name") || "Restaurant",
      invoiceNumber: item.invoice_number || "—",
      periodStart: pick(item, "period.start_date", "period_start"),
      periodEnd: pick(item, "period.end_date", "period_end"),
      memberSpend: normalizeAmount(pick(item, "total_member_spend", "performance_summary.eligible_member_spend", "settlement_calculation.eligible_member_spend")),
      platformFee: normalizeAmount(pick(item, "total_platform_fee", "pending_platform_fee", "settlement_calculation.platform_fee_due", "invoice_total")),
      amountDue: normalizeAmount(pick(item, "amount_due", "invoice_total")),
      status: item.invoice_status || item.status,
    }));
  }, [invoicesPayload]);

  // Update allBalanceRows when new data comes in
  useEffect(() => {
    if (page === 1) {
      setAllBalanceRows(balanceRows);
    } else {
      setAllBalanceRows((prev) => {
        const seen = new Set(prev.map((row) => row.id));
        return [...prev, ...balanceRows.filter((row) => !seen.has(row.id))];
      });
    }
  }, [balanceRows, page]);

  const filteredRows = useMemo(() => {
    return allBalanceRows;
  }, [allBalanceRows]);

  const settlementColumns = useMemo(() => [
    {
      key: "restaurant", label: "Restaurant", width: "1.6fr",
      render: (row) => (
        <span className="stl-table-restaurant">
          <span className="stl-table-restaurant__avatar">{row.restaurantName.slice(0, 1).toUpperCase()}</span>
          {row.restaurantName}
        </span>
      ),
    },
    { key: "invoiceNumber", label: "Invoice #", width: "1.2fr", render: (row) => row.invoiceNumber },
    { key: "period", label: "Period", width: "1.2fr", render: (row) => `${formatDateShort(row.periodStart)} – ${formatDateShort(row.periodEnd)}` },
    { key: "memberSpend", label: "Member Spend", width: "1.1fr", align: "right", render: (row) => currency(row.memberSpend) },
    { key: "platformFee", label: "Platform Fee", width: "1.1fr", align: "right", render: (row) => currency(row.platformFee) },
    { key: "amountDue", label: "Amount Due", width: "1.1fr", align: "right", render: (row) => currency(row.amountDue) },
    { key: "status", label: "Status", width: "0.9fr", align: "center", render: (row) => <StatusBadge status={row.status} /> },
    {
      key: "actions", label: "", width: "1.1fr", align: "right",
      render: (row) => (
        <span className="stl-row-actions">
          <SecondaryButton variant="light-grey" onClick={() => navigate(`/restaurant/settlements/invoices/${row.id}`)} icon={arrowRight}>View</SecondaryButton>
        </span>
      ),
    },
  ], [navigate]);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      setLoadingMore(true);
      setPage((prev) => prev + 1);
    }
  };



  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="stl-page">
          {/* ---- Header ---- */}
          <div className="adb-header">
            <div>
              <h1 className="adb-header__title">Settlements</h1>
              <p className="adb-header__subtitle">Review dine-in transactions, reward redemptions, and weekly settlement summary.</p>
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <Dropdown isOpen={periodMenuOpen} toggle={() => setPeriodMenuOpen((o) => !o)}>
                <DropdownToggle tag="div">
                  <button className="adb-period-btn"><IconCalendar /> {getPeriodLabel(period)} <IconChevronDown /></button>
                </DropdownToggle>
                <DropdownMenu end>
                  {Object.entries(PERIOD_LABELS).map(([key, label]) => (
                    <DropdownItem key={key} active={period === key} onClick={() => { setPeriod(key); setPage(1); setAllBalanceRows([]); setHasMore(true); if (key !== "custom") setDateRange({ preset: "custom", start: "", end: "" }); }}>{label}</DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
              {period === "custom" && (
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => { setDateRange({ ...dateRange, start: e.target.value }); setPage(1); setAllBalanceRows([]); setHasMore(true); }}
                    style={{ padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
                  />
                  <span style={{ color: "#6b7280" }}>to</span>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => { setDateRange({ ...dateRange, end: e.target.value }); setPage(1); setAllBalanceRows([]); setHasMore(true); }}
                    style={{ padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* ---- Top 4 stat cards ---- */}
          <div className="stl-stats-grid">
            <SummaryStatCard
              title="Total Member Spend" titleColor="var(--bg-dark-blue)" icon={<IconPeople />} iconBg="var(--bg-light-blue)"
              value={currency(totalMemberSpend)} deltaPct={deltaMemberSpend}
              secondaryLabel="Total Transactions" secondaryValue={number(totalTransactions)}
              periodLabel={getPeriodLabel(period)}
            />
            <SummaryStatCard
              title="Total Redeemed by Members" titleColor="#1ba97f" icon={<IconGift />} iconBg="var(--bg-sea-green)"
              value={currency(totalRedeemed)} deltaPct={deltaRedeemed}
              secondaryLabel="Total Redemptions" secondaryValue={number(totalRedemptions)}
              periodLabel={getPeriodLabel(period)}
            />
            <SummaryStatCard
              title={`Platform Fee (${feeRatePct}%)`} titleColor="var(--text-orange)" icon={<IconPercent />} iconBg="var(--bg-light-red)"
              value={currency(platformFee)} deltaPct={deltaFee}
              secondaryLabel="Settlement Amount (Payable to Platform)" secondaryValue={currency(settlementAmountPayable)}
              periodLabel={getPeriodLabel(period)}
            />
            <SummaryStatCard
              title="Net Restaurant Revenue" titleColor="#7b4fd6" icon={<IconDollarCircle />} iconBg="#f1ecfc"
              value={currency(netRestaurantRevenue)} deltaPct={deltaNetRevenue}
              periodLabel={getPeriodLabel(period)}
            />
          </div>

          {/* ---- Settlement Status ---- */}
          <div className="rp-card stl-card stl-status-card">
            <div className="rp-card__header">
              <div>
                <h2 className="rp-card__title stl-section-title">Settlement Status <span className="stl-muted-title">({getPeriodLabel(period)})</span></h2>
                <p className="rp-card__subtitle">Breakdown of settlement completion status.</p>
              </div>
            </div>
            {statusBreakdown ? (
              <>
                <div className="stl-donut-wrap">
                  <Donut segments={statusBreakdown} centerValue={number(totalRestaurantsSettled)} centerLabel="Total Settlements" />
                  <div className="stl-donut-legend">
                    {statusBreakdown.map((seg) => (
                      <div key={seg.label} className="stl-donut-legend__row">
                        <span className="stl-donut-legend__dot" style={{ background: seg.color }} />
                        <span className="stl-donut-legend__label">{seg.label}</span>
                        <span className="stl-donut-legend__count">{seg.count} ({seg.pct}%)</span>
                        <span className="stl-donut-legend__amount">{currency(seg.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="stl-settlement-amount-box">
                  <span>Settlement Amount (Payable to Platform)</span>
                  <strong>{currency(settlementAmountPayable)}</strong>
                </div>
              </>
            ) : (
              <EmptyState
                title="No Settlements"
                message="There are no settlements for this period yet."
              />
            )}
          </div>

          {/* ---- Restaurant Settlements ---- */}
          <div className="rp-card stl-card stl-table-card">
              <div className="rp-card__header stl-table-header">
                <div>
                  <h2 className="rp-card__title">Restaurant Settlements</h2>
                  <p className="rp-card__subtitle">Detailed breakdown of settlement transactions.</p>
                </div>
              </div>

              <div className="ar-table-scroll" style={{ overflowX: "auto" }}>
                <DataTable
                  columns={settlementColumns}
                  rows={filteredRows}
                  getRowKey={(r) => r.id}
                  loading={loading}
                  loadingMore={loadingMore}
                  onLoadMore={handleLoadMore}
                  hasMore={hasMore}
                  compact={true}
                />
              </div>
            </div>

        </div>
      </Container>
    </div>
  );
};

export default SettlementDashboardPage;
